import React, { useMemo } from 'react';
import { Box, Container, Header, Spinner } from '@amzn/awsui-components-core';
import { ConnectionsScorecard, CONNECTIONS_THEMES, THEME_DISPLAY_NAMES, Theme } from '../types';
import './highlights-lowlights.scss';

interface HighlightsLowlightsProps {
  loading: boolean;
  error: Error | null;
  scorecards: ConnectionsScorecard[];
  infoLink?: React.ReactNode;
  sectionContext?: string;
}

interface LeaderThemeRow {
  leaderLogin: string;
  displayName: string;
  theme: Theme;
  metricValue: number;
  benchmarkMin: number;
  benchmarkMax: number;
}

const formatPct = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '-';
  return `${(n * 100).toFixed(1)}%`;
};

const TOP_N = 5;

/**
 * Flatten direct-org scorecards into "leader × theme" rows, then split into
 * highlights (above expected range) and lowlights (below expected range). Surfaces the
 * top N of each so the user immediately sees who's standing out — both ways.
 */
export const HighlightsLowlights: React.FC<HighlightsLowlightsProps> = ({ loading, error, scorecards, infoLink, sectionContext }) => {
  const { highlights, lowlights } = useMemo(() => {
    const highs: LeaderThemeRow[] = [];
    const lows: LeaderThemeRow[] = [];
    for (const sc of scorecards) {
      const display = sc.displayName || sc.leaderLogin;
      for (const t of sc.themes) {
        const themeUpper = t.theme.toUpperCase() as Theme;
        if (!CONNECTIONS_THEMES.includes(themeUpper)) continue;
        if (t.metricValue == null || t.benchmarkMin == null || t.benchmarkMax == null) continue;
        const row: LeaderThemeRow = {
          leaderLogin: sc.leaderLogin,
          displayName: display,
          theme: themeUpper,
          metricValue: t.metricValue,
          benchmarkMin: t.benchmarkMin,
          benchmarkMax: t.benchmarkMax
        };
        if (t.metricValue > t.benchmarkMax) {
          highs.push(row);
        } else if (t.metricValue < t.benchmarkMin) {
          lows.push(row);
        }
      }
    }
    // Sort: highlights by metric DESC, lowlights by metric ASC (most extreme first)
    highs.sort((a, b) => b.metricValue - a.metricValue);
    lows.sort((a, b) => a.metricValue - b.metricValue);
    return { highlights: highs.slice(0, TOP_N), lowlights: lows.slice(0, TOP_N) };
  }, [scorecards]);

  if (loading) {
    return (
      <Container header={<Header variant="h2">✅ Highlights / ⚠️ Lowlights</Header>}>
        <Box textAlign="center" padding="xl">
          <Spinner size="big" /> Loading...
        </Box>
      </Container>
    );
  }
  if (error) {
    return (
      <Container header={<Header variant="h2">✅ Highlights / ⚠️ Lowlights</Header>}>
        <Box color="text-status-error" padding="m">
          {error.message}
        </Box>
      </Container>
    );
  }

  return (
    <div className="hl-grid">
      <Container
        header={
          <Header
            variant="h2"
            actions={<span className="hl-context">{sectionContext}</span>}
            description={`Top ${TOP_N} leader × theme rows ABOVE expected range`}
            info={infoLink}
          >
            ✅ Highlights
          </Header>
        }
      >
        {highlights.length === 0 ? (
          <Box color="text-body-secondary" padding="s">
            No directs above expected range for the selected period.
          </Box>
        ) : (
          <div className="hl-list">
            {highlights.map((r, i) => (
              <div key={`${r.leaderLogin}-${r.theme}-${i}`} className="hl-row hl-row--high">
                <div>
                  <div className="hl-name">{r.displayName}</div>
                  <div className="hl-sub">
                    {THEME_DISPLAY_NAMES[r.theme]} · max {formatPct(r.benchmarkMax)}
                  </div>
                </div>
                <div className="hl-value hl-value--high">{formatPct(r.metricValue)}</div>
              </div>
            ))}
          </div>
        )}
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            actions={<span className="hl-context">{sectionContext}</span>}
            description={`Top ${TOP_N} leader × theme rows BELOW expected range`}
          >
            ⚠️ Lowlights
          </Header>
        }
      >
        {lowlights.length === 0 ? (
          <Box color="text-body-secondary" padding="s">
            No directs below expected range for the selected period.
          </Box>
        ) : (
          <div className="hl-list">
            {lowlights.map((r, i) => (
              <div key={`${r.leaderLogin}-${r.theme}-${i}`} className="hl-row hl-row--low">
                <div>
                  <div className="hl-name">{r.displayName}</div>
                  <div className="hl-sub">
                    {THEME_DISPLAY_NAMES[r.theme]} · min {formatPct(r.benchmarkMin)}
                  </div>
                </div>
                <div className="hl-value hl-value--low">{formatPct(r.metricValue)}</div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
};
